import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * The root panel is a native `<dialog>` (`ref="panel"`), opened via `showModal()`. This
 * promotes it to the browser's top layer, so it always renders above any third-party
 * app content injected elsewhere on the page (e.g. a shipping/announcement bar app
 * block with its own self-applied `position: fixed` + high `z-index`) regardless of
 * CSS stacking-context quirks that differ by browser/device. This mirrors the approach
 * already used by the cart drawer (`theme-drawer` + `<dialog>`).
 *
 * Nested submenu accordions remain plain `<details>` elements (unchanged behaviour).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The header hamburger button that opens the drawer.
 * @property {HTMLDialogElement} panel - The dialog element housing the drawer content.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['trigger', 'panel', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeRootListeners();
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.panel.open;
  }

  /**
   * Get the nested submenu details element closest to the event target, if any.
   * Returns null when the event didn't originate inside a submenu (i.e. the
   * call concerns the root drawer itself).
   *
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement | null}
   */
  #getSubmenuDetails(event) {
    if (!(event?.target instanceof Element)) return null;
    return event.target.closest('details');
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest submenu, or the root drawer when called with no event
   * (e.g. via toggle() from the hamburger button).
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getSubmenuDetails(event);

    if (details) {
      const summary = details.querySelector('summary');
      if (!summary) return;

      summary.setAttribute('aria-expanded', 'true');

      this.preventInitialAccordionAnimations(details);
      requestAnimationFrame(() => {
        details.classList.add('menu-open');

        if (target) {
          this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
        }

        const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
        onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
      });
      return;
    }

    this.#openRoot();
  }

  /**
   * Go back (closest open submenu) or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    const details = this.#getSubmenuDetails(event);

    if (details) {
      this.#closeSubmenu(details);
    } else {
      this.close();
    }
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#closeRoot();
  }

  /**
   * Opens the root drawer dialog.
   */
  #openRoot() {
    const { panel, trigger, menuDrawer } = this.refs;
    if (panel.open) return;

    trigger.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(panel);

    lockScroll(panel);
    panel.showModal();

    document.addEventListener('keydown', this.#onKeyDown);

    requestAnimationFrame(() => {
      panel.classList.add('menu-open');
      onAnimationEnd(menuDrawer, () => trapFocus(panel), { subtree: false });
    });
  }

  /**
   * Closes the root drawer dialog, resetting any open submenu accordions.
   */
  #closeRoot() {
    const { panel, trigger, menuDrawer } = this.refs;
    if (!panel.open) return;

    trigger.setAttribute('aria-expanded', 'false');
    panel.classList.remove('menu-open');
    menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Idempotency guard: the animation-end handler and the timeout fallback below
    // can both fire (e.g. if getAnimations() misses the transition on this pass),
    // so only the first one to run should actually finalize the close.
    let finalized = false;
    const finalize = () => {
      if (finalized) return;
      finalized = true;

      const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
      openDetails.forEach(reset);

      removeTrapFocus();
      this.#removeRootListeners();
      unlockScroll(panel);
      if (panel.open) panel.close();
    };

    onAnimationEnd(menuDrawer, finalize, { subtree: false });
    // Safety net: never leave the dialog open (unresponsive close button) if the
    // animation-end detection doesn't resolve for any reason.
    setTimeout(finalize, 500);
  }

  #removeRootListeners() {
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Closes the nearest open submenu (or the root drawer) when Escape is pressed.
   * Only listens while the root drawer is open (added/removed in #openRoot/#closeRoot).
   * preventDefault() suppresses the dialog's native Escape-triggered close, so the
   * drawer's own close animation always runs instead of the dialog snapping shut.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (event.defaultPrevented) return;

    event.preventDefault();

    const details = this.#getSubmenuDetails(event);
    if (details) {
      this.#closeSubmenu(details);
    } else {
      this.#closeRoot();
    }
  };

  /**
   * Closes an open submenu accordion.
   * @param {HTMLDetailsElement} details
   */
  #closeSubmenu(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    let finalized = false;
    const finalize = () => {
      if (finalized) return;
      finalized = true;
      reset(details);
      trapFocus(this.refs.panel);
    };
    onAnimationEnd(drawer || details, finalize, { subtree: false });
    setTimeout(finalize, 500);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLElement} details - The details element (or dialog) containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
